import AuthenticationService from "application/modules/authentication/AuthenticationService";
import { reportMenus } from "globals/configuration/reports";
import { ExtensionReportMenus } from "globals/configuration/reports/ExtensionReports";
import { billingUserMenus } from "globals/configuration/reports/billingUserMenus";

const buildModes = () => {
  const source = AuthenticationService.isBillingOnlyUser()
    ? billingUserMenus
    : AuthenticationService.allowApollo()
      ? ExtensionReportMenus
      : reportMenus;
  return source.map((item) => ({
    id: item.key,
    name: item.name,
    iconName: item.icon,
    url: item.url,
    description: item.description,
    disabled: item.disabled,
    productionReady: item.productionReady,
  }));
};

const Attributes = {
  enableAddButton: false,
  enableUpdateTableRecords: false,
  enableDownloadButton: false,
  enableAdvanceSearch: false,
  enableDownloadPdf: false,
  enableRefresh: false,
  // getter so the role check runs at access-time (after login populates roles),
  // not at module-eval time during app bootstrap.
  get modes() {
    return buildModes();
  },
};

const operationURLS = {
  list: "/cdr/raw/list",
  count: "/cdr/raw/count",
  export: "/cdr/raw/export",
  detail: "/cdr/raw/detail",
  related: "/cdr/related",
};

// Grid columns. accessor maps to RawCdrRow fields. All default-visible — the
// grid container scrolls horizontally so all columns are reachable.
const gridColumns = [
  { header: "Leg", accessor: "leg" },
  { header: "Start Stamp", accessor: "startEpoch", type: "datetime" },
  { header: "Answer Stamp", accessor: "answerEpoch", type: "datetime" },
  { header: "End Stamp", accessor: "endEpoch", type: "datetime" },
  { header: "Caller", accessor: "callerIdNumber", sub: "callerIdName" },
  { header: "Initial Caller", accessor: "initialCidNum" },
  { header: "Account Code", accessor: "accountCode" },
  { header: "Called", accessor: "destinationNumber" },
  { header: "Initial Destination", accessor: "initialDest" },
  { header: "Duration", accessor: "duration", type: "mmss" },
  { header: "Talk Time", accessor: "billsec", type: "mmss" },
  { header: "Wait", accessor: "waitsec", type: "seconds" },
  { header: "HANGUPCAUSE", accessor: "hangupCause", type: "resultChip" },
  { header: "SIP Hangup", accessor: "sipHangupDisposition", type: "hangupBy" },
  { header: "Agent", accessor: "ccAgent" },
  { header: "Queue", accessor: "ccQueue" },
  { header: "Side", accessor: "ccSide" },
  { header: "Transfer By", accessor: "transferBy" },
  { header: "Transfer To", accessor: "transferTo" },
  { header: "Transfer Type", accessor: "transferType" },
  { header: "Transfer Status", accessor: "transferStatus" },
  { header: "Recording", accessor: "hasRecording", type: "bool" },
  { header: "Digits Dialed", accessor: "digitsDialed" },
];

// Related-CDR Dialog columns — same shape as the grid, with Anchor + Match
// Reason prepended/appended. Captions mirror the grid renames.
const relatedColumns = [
  { header: "Leg", accessor: "leg" },
  { header: "Anchor", accessor: "anchor", type: "anchor" },
  { header: "Start Stamp", accessor: "startEpoch", type: "datetime" },
  { header: "Answer Stamp", accessor: "answerEpoch", type: "datetime" },
  { header: "End Stamp", accessor: "endEpoch", type: "datetime" },
  { header: "Caller", accessor: "callerIdNumber" },
  { header: "Account Code", accessor: "accountCode" },
  { header: "Called", accessor: "destinationNumber" },
  { header: "Duration", accessor: "duration", type: "mmss" },
  { header: "Talk Time", accessor: "billsec", type: "mmss" },
  { header: "Wait", accessor: "waitsec", type: "seconds" },
  { header: "HANGUPCAUSE", accessor: "hangupCause", type: "resultChip" },
  { header: "SIP Hangup", accessor: "sipHangupDisposition", type: "hangupBy" },
  { header: "Agent", accessor: "ccAgent" },
  { header: "Queue", accessor: "ccQueue" },
  { header: "Side", accessor: "ccSide" },
  { header: "Transfer By", accessor: "transferBy" },
  { header: "Transfer To", accessor: "transferTo" },
  { header: "Transfer Type", accessor: "transferType" },
  { header: "Transfer Status", accessor: "transferStatus" },
  { header: "Digits Dialed", accessor: "digitsDialed" },
  { header: "Match Reason", accessor: "matchReason" },
];

// Detail drawer field groups. Keys are the raw t_cdr column names returned by
// /cdr/raw/detail (c.*). Missing keys are skipped; a group with no present
// values is hidden entirely. callid/sip_call_id are absent in coralapps.
const detailGroups = [
  {
    title: "Identity & Leg",
    fields: [
      { label: "UUID", key: "uuid" },
      { label: "Leg", key: "leg" },
      { label: "Bridge UUID", key: "bridge_uuid" },
      { label: "B-Leg UUID", key: "bleg_uuid" },
      { label: "Originating Leg UUID", key: "originating_leg_uuid" },
    ],
  },
  {
    title: "Participants",
    fields: [
      { label: "Caller Number", key: "caller_id_number" },
      { label: "Caller Name", key: "caller_id_name" },
      { label: "Initial Caller", key: "initial_cid_num" },
      { label: "Account Code", key: "accountcode" },
      { label: "Destination Number", key: "destination_number" },
      { label: "Initial Destination", key: "initial_dest" },
      { label: "Destination Name", key: "destination_name" },
    ],
  },
  {
    title: "Timing",
    fields: [
      { label: "Start", key: "start_epoch", type: "datetime" },
      { label: "Answer", key: "answer_epoch", type: "datetime" },
      { label: "End", key: "end_epoch", type: "datetime" },
      { label: "Duration", key: "duration" },
      { label: "Talk Time", key: "billsec" },
      { label: "Wait", key: "waitsec" },
      { label: "PDD (ms)", key: "pdd_ms" },
    ],
  },
  {
    title: "HANGUPCAUSE",
    fields: [
      { label: "Hangup Cause", key: "hangup_cause" },
      { label: "Hangup Cause Q.850", key: "hangup_cause_q850" },
      { label: "Hangup By", key: "sip_hangup_disposition" },
      { label: "Last App", key: "last_app" },
      { label: "Last Arg", key: "last_arg" },
    ],
  },
  {
    title: "Call Center",
    fields: [
      { label: "Side", key: "cc_side" },
      { label: "Queue", key: "cc_queue" },
      { label: "Agent", key: "cc_agent" },
      { label: "Member UUID", key: "cc_member_uuid" },
      { label: "Member Session UUID", key: "cc_member_session_uuid" },
    ],
  },
  {
    title: "Audio Quality",
    fields: [
      { label: "MOS", key: "rtp_audio_in_mos" },
      { label: "Quality %", key: "rtp_audio_in_quality_percentage" },
      { label: "Codec", key: "rtp_use_codec_name" },
    ],
  },
  {
    title: "Gateway / Server",
    fields: [
      { label: "Server Code", key: "servercode" },
      { label: "Network Addr", key: "network_addr" },
    ],
  },
  {
    title: "Other",
    fields: [
      { label: "Digits Dialed", key: "digits_dialed" },
      { label: "Recording File", key: "cc_record_filename" },
      { label: "Remarks", key: "userremarks" },
    ],
  },
];

export { Attributes, operationURLS, gridColumns, relatedColumns, detailGroups };
